import GirlCard from '../cards/girlCard/GirlCard';
import { Button, Icon, Loader } from '@/components/ui';
import { useEffect, useState } from 'react';
import {
	GoogleMap,
	Marker,
	MarkerClusterer,
	InfoBox,
} from '@react-google-maps/api';
import { useDispatch, useSelector } from 'react-redux';
import {
	getGmapsApiReady,
	getMapModel,
	getOneModelPosition,
	setGmapsApiReady,
	setMapModel,
	setOneModelPosition,
} from '@/stores/slices/mapModelSlice';
import './mapFilter.scss';
import { getMapStatus, setStoriesMap } from '@/stores/slices/popupSlice';
import { useTranslation } from 'react-i18next';
import { makeRequest } from '@/services/makeRequest';
import useLadyService from '@/services/LadyService';
import clusterIcon from '@/assets/img/icons/cluster.svg';
import axios from 'axios';
import baseUrl from '@/services/apiConfig';
import { Popup } from '../popups';
import showToast from '../toast/Toast';
import {
	getCurrentLinkCity,
	getUserCurrentCity,
} from '@/stores/slices/userSlice';
import { getFilterState } from '@/stores/slices/filterSlice';
import { buildFilterParams } from '../filter/FilterParameterBuilder';
import { useParams } from 'react-router-dom';
import { UrlAllowedWords } from '../filter/FilterData';

const MyMap = ({ setClusterModels, dataCluster }) => {
	const { dispatch, lang } = useLadyService();
	const params = useParams();

	const mapStyles = {
		height: '100%',
		width: '100%',
	};

	const oneModel = useSelector(getOneModelPosition);
	const filterState = useSelector(getFilterState);

	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isLoad, setIsLoad] = useState(true);
	const [icon, setIcon] = useState(null);
	const [map, setMap] = useState(null);

	const { t } = useTranslation('translation');
	const currentCity = useSelector(getUserCurrentCity);
	const currentLinksCity = useSelector(getCurrentLinkCity);

	const toggleCurrentTippy = (e) => {
		const target = e.target;
		document.querySelectorAll('.map-filter__tippy').forEach((item) => {
			item.classList.remove('current-tippy');
		});
		target.classList.add('current-tippy');
	};

	const apiReady = useSelector(getGmapsApiReady);

	const mapLink = `https://maps.googleapis.com/maps/api/js?key=AIzaSyA81vyhvrBl3jUTFDK6LwYNm317prcxfVo&libraries=places&language=en`;

	const loadScript = async (retryCount = 0) => {
		return new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = mapLink;
			script.async = true;

			script.onload = () => {
				// console.log("%cGoogle Maps API loaded successfully. ", "color: green; font-size: 8px");
				dispatch(setGmapsApiReady(true));
				resolve();
			};

			script.onerror = () => {
				// console.error("%cGoogle Maps API failed to load.", "color: red; font-size: 8px");
				if (retryCount < 5) {
					// console.log(`%cRetrying to load Google Maps API... Attempt: ${retryCount + 1}`, "color: orange; font-size: 8px");
					setTimeout(() => {
						loadScript(retryCount + 1)
							.then(resolve)
							.catch(reject);
					}, 1000);
				} else {
					reject(
						new Error(
							'Google Maps API failed to load after multiple attempts.',
						),
					);
				}
			};

			document.body.appendChild(script);
		});
	};

	useEffect(() => {
		if (!apiReady) {
			loadScript()
				.then(() => {
					// console.log("%cGoogle Maps API loaded successfully. ", "color: green; font-size: 8px");
					dispatch(setGmapsApiReady(true));
				})
				.catch((error) => {
					console.error(
						`%cError loading Google Maps API: ${error}`,
						'color: red; font-size: 8px',
					);
				});
		}
	}, []);

	// Fetch map markers
	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);

			const { result } = buildFilterParams(
				filterState,
				filterState.ranges.default_state,
				params?.params ?? '',
				UrlAllowedWords,
			);

			try {
				const route = `profiles/simply/${currentCity.slug}`;
				const method = 'GET';
				const queryParams = Object.fromEntries(
					new URLSearchParams(result.join('&')),
				);

				const payload = {
					...queryParams,
					...(!!oneModel && { slug: oneModel }),
					...(filterState.service_type.includes('massage') && {
						massage: '1',
					}),
				};

				const response = await makeRequest({ route, method, payload });
				if (response) {
					setData(response.data);
				}
			} catch (error) {
				showToast({
					message: t('oops'),
					variant: 'error',
				});
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	// Update map bounds when data or oneModel changes
	useEffect(() => {
		if (!map || data.length === 0) return;

		const bounds = new window.google.maps.LatLngBounds();

		if (!oneModel) {
			// Fit all markers
			data.forEach((model) => {
				if (model.latitude && model.longitude) {
					bounds.extend(
						new window.google.maps.LatLng(
							model.latitude,
							model.longitude,
						),
					);
				}
			});
			map.fitBounds(bounds);
		} else {
			// Fit only the selected model
			const modelCoords = data.filter((item) => item.slug === oneModel);
			modelCoords.forEach((model) => {
				if (model.latitude && model.longitude) {
					bounds.extend(
						new window.google.maps.LatLng(
							model.latitude,
							model.longitude,
						),
					);
				}
			});
			map.setZoom(18);
			map.fitBounds(bounds);
		}

		// Update marker icon
		setIcon({
			path: 'M0 0h24v24H0z',
			fillColor: 'transparent',
			fillOpacity: 0,
			strokeWeight: 0,
			scale: 0,
		});
		setIsLoad(false);

		// Cap zoom after idle
		const listener = map.addListener('idle', () => {
			if (map.getZoom() > 16) {
				map.setZoom(16);
			}
			window.google.maps.event.removeListener(listener);
		});

		return () => {
			window.google.maps.event.removeListener(listener);
		};
	}, [map, data, oneModel]);

	const clusterStyles = [
		{
			textSize: 22,
			textColor: 'white',
			url: clusterIcon,
			height: 60,
			width: 60,
		},
		{
			textSize: 22,
			textColor: 'white',
			url: clusterIcon,
			height: 60,
			width: 60,
		},
		{
			textSize: 22,
			textColor: 'white',
			url: clusterIcon,
			height: 60,
			width: 60,
		},
	];

	const handleClusterClick = (cluster) => {
		console.log(`=> click cluster: `, cluster);
		// Only show cluster panel if zoomed in enough and no cluster already open

		if (map.getZoom() >= 16) {
			const markers = cluster.getMarkers();
			dispatch(setMapModel(null));
			dispatch(setOneModelPosition(null));

			console.log(`=> click cluster fall in: `);

			const allSlugs = markers
				.map((marker) => marker.model?.slug)
				.filter(Boolean);

			axios
				.get(`${baseUrl}profiles?slug=${allSlugs.join(',')}`)
				.then((response) => {
					if (response.data.profiles) {
						// Pass full model objects with slug property
						setClusterModels(response.data.profiles);
						console.log(`=> click cluster should set: `);
					}
				})
				.catch((error) => {
					console.error(error);
					showToast({
						message: t('oops'),
						subMessage: `server error ${error.response?.status}`,
						variant: 'error',
					});
				});
		} else {
			console.log(
				`=> click cluster fallout: `,
				map.getZoom(),
				dataCluster.length,
			);
		}
	};

	const options = {
		maxZoom: 19,
		gestureHandling: 'greedy',
	};

	const setCityCentered = (map) => {
		if (!currentLinksCity || oneModel) return;

		const geocoder = new window.google.maps.Geocoder();
		geocoder.geocode(
			{ address: currentLinksCity.slug },
			(results, status) => {
				if (status === 'OK' && results.length > 0) {
					map.setCenter(results[0].geometry.location);
					map.setZoom(10);
				} else {
					console.error('Geocoder failed due to: ' + status);
				}
			},
		);
	};

	const loadMap = (map) => {
		setMap(map);
		setTimeout(() => {
			setCityCentered(map);
		}, 200);
	};

	const getMarkerClass = (model) => {
		if (model.is_elite && !model.is_top)
			return 'map-filter__tippy map-filter__tippy--elite';
		if (!model.is_elite && model.is_top)
			return 'map-filter__tippy map-filter__tippy--top';
		return 'map-filter__tippy';
	};

	if (loading) return <Loader />;

	return (
		<GoogleMap
			mapContainerStyle={mapStyles}
			onLoad={loadMap}
			options={options}
		>
			<MarkerClusterer
				gridSize={60}
				enableRetinaIcons={true}
				styles={clusterStyles}
				onClick={handleClusterClick}
			>
				{(clusterer) =>
					data.map((model) => {
						if (!model.latitude || !model.longitude) return null;

						return (
							<Marker
								icon={icon}
								key={model.id}
								position={{
									lat: model.latitude,
									lng: model.longitude,
								}}
								clusterer={clusterer}
								options={{ model }}
							>
								{!isLoad && (
									<InfoBox
										defaultPosition={
											new window.google.maps.LatLng(
												model.latitude,
												model.longitude,
											)
										}
										options={{
											enableEventPropagation: false,
											boxStyle: {
												translate: '-50% -127%',
												minWidth: '100px',
											},
											closeBoxURL: '',
										}}
									>
										<div
											onClick={(e) => {
												e.stopPropagation();
												setClusterModels([]);
												dispatch(
													setMapModel(model.slug),
												);
												toggleCurrentTippy(e);
											}}
											className={getMarkerClass(model)}
										>
											{`${t('from')} `}
											{model.prices?.incall ??
												model.prices?.outcall}
											{lang === 'cz' ? ' Kč' : ' €'}
											{`/${t('hour')}`}
										</div>
									</InfoBox>
								)}
							</Marker>
						);
					})
				}
			</MarkerClusterer>
		</GoogleMap>
	);
};

const ClusterTemplate = ({
	mapOpen,
	clusterModels, // full model objects from cluster click [{slug, ...}, ...]
	setClusterModels,
	clusterProfiles, // full profile data for sidebar display
	setClusterProfiles,
}) => {
	const { dispatch, windowWidth } = useLadyService();
	const currentModelId = useSelector(getMapModel);
	const oneModel = useSelector(getOneModelPosition);
	const [load, setLoad] = useState(true);

	// Fetch a single profile by slug and set it in clusterProfiles
	const fetchProfile = async (slug) => {
		try {
			const route = `profiles/short/${slug}`;
			const data = await makeRequest({ route, method: 'GET' });
			if (data) {
				return data.data;
			}
		} catch (error) {
			console.error('Failed to fetch profile:', error);
		}
		return null;
	};

	// When a marker tippy is clicked -> load that single profile
	useEffect(() => {
		if (!currentModelId) {
			setClusterProfiles(null);
			return;
		}

		const load = async () => {
			setLoad(false);
			const profile = await fetchProfile(currentModelId);
			if (profile) {
				setClusterProfiles([profile]);
			}
			setLoad(true);
		};

		load();
	}, [currentModelId]);

	// When cluster is clicked -> load all profiles in that cluster
	useEffect(() => {
		if (!clusterModels?.length) return;

		const loadAll = async () => {
			setLoad(false);
			// Reset before loading new cluster profiles
			setClusterProfiles([]);

			const profiles = await Promise.all(
				clusterModels.map((model) => fetchProfile(model.slug)),
			);

			setClusterProfiles(profiles.filter(Boolean));
			setLoad(true);
		};

		loadAll();
	}, [clusterModels]);

	// Load oneModel profile on desktop
	useEffect(() => {
		if (windowWidth <= 768 || !oneModel) return;

		const load = async () => {
			setLoad(false);
			const profile = await fetchProfile(oneModel);
			if (profile) {
				setClusterProfiles([profile]);
			}
			setLoad(true);
		};

		load();
	}, [oneModel, windowWidth]);

	// Cleanup when map closes
	useEffect(() => {
		if (!mapOpen) {
			setClusterProfiles(null);
			setClusterModels([]);
		}
	}, [mapOpen]);

	const handleClose = () => {
		setClusterProfiles(null);
		setClusterModels([]);
		dispatch(setMapModel(null));
		document.querySelectorAll('.map-filter__tippy').forEach((item) => {
			item.classList.remove('current-tippy');
		});
	};

	if (!clusterProfiles) return null;

	return (
		<div className={'map-filter__wrapper'}>
			{!load ? (
				<div className="girl-card-loader">
					<Loader />
				</div>
			) : (
				<>
					<Button
						size={'m'}
						square={true}
						clazz={'button--primary button-icon'}
						onClick={handleClose}
					>
						<Icon
							spritePath={'close'}
							size={'m'}
						/>
					</Button>
					<div className={'clusters'}>
						{clusterProfiles.map((item) => (
							<GirlCard
								topModelsSlider={true}
								props={{ ...item }}
								key={item.id}
								mobile={true}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
};

const MapFilter = () => {
	const dispatch = useDispatch();
	const mapPopupStatus = useSelector(getMapStatus);

	const onClose = () => {
		dispatch(setStoriesMap(false));
		dispatch(setMapModel(null));
		dispatch(setOneModelPosition(null));
	};

	// Raw model objects returned from cluster click API
	const [clusterModels, setClusterModels] = useState([]);
	// Full profile data to display in the sidebar
	const [clusterProfiles, setClusterProfiles] = useState(null);

	return (
		<Popup
			open={!!mapPopupStatus}
			setOpen={() => onClose()}
			id={'map'}
			clazz="map-filter__body"
			container={'div'}
		>
			{mapPopupStatus && (
				<MyMap
					setClusterModels={setClusterModels}
					dataCluster={clusterModels}
				/>
			)}
			<ClusterTemplate
				mapOpen={mapPopupStatus}
				clusterModels={clusterModels}
				setClusterModels={setClusterModels}
				clusterProfiles={clusterProfiles}
				setClusterProfiles={setClusterProfiles}
			/>
		</Popup>
	);
};

export default MapFilter;
